import { Subject, Observable } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera, SnCameraGroup } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-camera-search-dialog',
  templateUrl: './camera-search-dialog.component.html',
  styleUrls: ['./camera-search-dialog.component.scss'],
})
export class CameraSearchDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnCamera>;
  public readonly loadData = this._loadData.bind(this);
  public readonly feedData = this._feedData.bind(this);
  public selection = new SelectionModel<SnCamera>(true, []);
  public cameraGroups: SnCameraGroup[];

  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public readonly columns: string[] = ['name', 'location', 'floor', 'owner'];
  public _querySearch: string;

  public pageSize;

  public selected;

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<CameraSearchDialogComponent>,
    private readonly _camerasAPI: SnCamerasAPI,
  ) {
    if (this.data?.multiple) {
      this.columns.unshift('select');
    }
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });

    this.selected = '20';
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._querySearch = this.searchControl.value;
    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.submitForm();
  }

  public onSelected(camera: SnCamera): void {
    if (this.data?.multiple) {
      this.selection.toggle(camera);
      if (camera.descendantCameras?.length) {
        camera.descendantCameras.forEach((descendant) => {
          this.selection.toggle(descendant);
        });
      }
    } else {
      if (camera.descendantCameras?.length) {
        this._dialogRef.close([camera, ...camera.descendantCameras]);
      } else {
        this._dialogRef.close(camera);
      }
    }
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public loadAllLocations(): void {
    this.pageSize = 'all';
    this._reloadData();
  }

  public loadTenLocations(): void {
    this.pageSize = 10;
    this._reloadData();
  }

  public loadTwentyLocations(): void {
    this.pageSize = 20;
    this._reloadData();
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.list.dataSource.data.length;
    return numSelected === numRows;
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.list.dataSource.data.forEach((row) => this.selection.select(row));
  }

  public addCameras(): void {
    const cameras = this.selection.selected.map((camera) => camera);
    this._dialogRef.close(cameras);
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    params.search = this._querySearch;
    return this._camerasAPI.getCameraList(this._prepareQueryParams(params)).pipe(
      tap((response) => {
        if (this.data?.cameras?.length) {
          const targetCameras = response.results.filter(
            (c) => this.data.cameras.some((cm) => cm.id === c.id) && !this.selection.isSelected(c),
          );
          if (targetCameras.length) {
            this.selection.select(...targetCameras);
          }
        }
      }),
      takeUntil(this._destroy$),
    );
  }

  private _feedData(params: ISnFeedQueryParameters): Observable<SnCamera[]> {
    return this._camerasAPI.feedCameras(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: any): any {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      search: this._querySearch,
      page: params.page,
      pageSize: this.pageSize ? this.pageSize : params.pageSize,
    };
  }
}
